import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import {Tooltip} from 'react-tooltip';

import {useTranslations} from 'src/components/TranslationsContext';
import {H5ForecastListing, H5ModelVisibility} from 'src/lib/api/h5';

const BADGE_COLORS: Record<H5ModelVisibility, string> = {
  primary: 'bg-blue-50 text-blue-700 ring-blue-700/20',
  secondary: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  // Faint: only internal users see it, and it matters least of the three.
  internal: 'bg-gray-50 text-gray-400 ring-gray-500/10',
};

export function VisibilityBadge({visibility}: {visibility: H5ModelVisibility}) {
  const {t} = useTranslations();
  const label =
    visibility === 'primary'
      ? t('primaryForecastModel')
      : visibility === 'secondary'
        ? t('secondaryForecastModel')
        : t('internal');
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${BADGE_COLORS[visibility]}`}
    >
      {label}
    </span>
  );
}

// Which model a name stands for, for internal users: the ID the model goes by everywhere else,
// with a button to copy it, and the config signature and weather sources the name was read from.
export function ModelInfoTooltip({listing}: {listing: H5ForecastListing}) {
  const {t} = useTranslations();
  // Several are mounted at once, one per legend row, and react-tooltip anchors by a document-wide
  // id.
  const anchorId = React.useId();
  const [copied, setCopied] = React.useState(false);
  const components =
    listing.status === 'available' ? listing.modelConfigSignature?.components : undefined;
  const weatherSources = listing.status === 'available' ? listing.weatherSources : undefined;
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(listing.modelId);
    } catch {
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const CopyIcon = copied ? ClipboardDocumentCheckIcon : ClipboardDocumentIcon;
  return (
    <>
      <InformationCircleIcon
        className="h-4 w-4 shrink-0 text-gray-400"
        data-tooltip-id={anchorId}
      />
      <Tooltip
        id={anchorId}
        className="z-[9999] !bg-gray-50 !rounded-md"
        place="bottom"
        opacity={1}
        border="1px solid lightgray"
        positionStrategy="fixed"
        clickable
      >
        <div className="text-left text-xs text-black whitespace-nowrap">
          <div className="flex items-center gap-1 font-semibold">
            {`${listing.modelType} / ${listing.modelId}`}
            <button
              type="button"
              aria-label={copied ? t('copied') : t('copy')}
              title={copied ? t('copied') : t('copy')}
              className="text-gray-500 hover:text-gray-800"
              onClick={copyId}
            >
              <CopyIcon className="h-4 w-4" />
            </button>
          </div>
          {components && (
            <table className="mt-1">
              <tbody>
                {Object.entries(components).map(([slot, className]) => (
                  <tr key={slot}>
                    <td className="pr-2 text-gray-500">{slot}</td>
                    <td>{className}</td>
                  </tr>
                ))}
                {!!weatherSources?.length && (
                  <tr>
                    <td className="pr-2 text-gray-500">weather_sources</td>
                    <td>{weatherSources.join(', ')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Tooltip>
    </>
  );
}
